import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { PhoneOutgoing, PhoneIncoming, ChevronDown, ChevronUp } from 'lucide-react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import '@/styles/audio-player.css';
import { getCalls, getCallDetail } from '@/lib/calls';
import type { Call, CallDetail, CallFilters } from '@/lib/calls';
import { apiRequest } from '@/lib/api';
import { auth } from '@/lib/firebase';

// ─── Helpers ───

function formatDuration(seconds: number | null): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDateFrom(preset: string): string | undefined {
  const now = new Date();
  switch (preset) {
    case '24h': return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    case '7d': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    case '30d': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    case 'all': return undefined;
    default: return undefined;
  }
}

// ─── Recording Player ───

function RecordingPlayer({ recordingUrl }: { recordingUrl: string }) {
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    async function loadRecording() {
      const token = await auth.currentUser?.getIdToken();
      fetch(recordingUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.blob();
      })
      .then((blob) => setAudioSrc(URL.createObjectURL(blob)))
      .catch(() => setLoadError(true));
    }
    loadRecording();
  }, [recordingUrl]);

  const [speed, setSpeed] = useState(1);
  const playerRef = useRef<AudioPlayer>(null);

  const cycleSpeed = () => {
    const speeds = [1, 1.25, 1.5, 2];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
    setSpeed(next);
    const audio = playerRef.current?.audio?.current;
    if (audio) audio.playbackRate = next;
  };

  if (loadError) return <p className="text-xs text-muted-foreground">Recording unavailable</p>;
  if (!audioSrc) return <p className="text-xs text-muted-foreground">Loading recording...</p>;

  const skipBackIcon = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5V1L7 6l5 5V7a6 6 0 1 1-6 6" />
      <text x="12" y="16" textAnchor="middle" fill="currentColor" stroke="none" fontSize="7" fontWeight="bold">10</text>
    </svg>
  );

  const skipForwardIcon = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5V1l5 5-5 5V7a6 6 0 1 0 6 6" />
      <text x="12" y="16" textAnchor="middle" fill="currentColor" stroke="none" fontSize="7" fontWeight="bold">10</text>
    </svg>
  );

  return (
    <div className="max-w-lg">
      <AudioPlayer
        ref={playerRef}
        src={audioSrc}
        showJumpControls={true}
        progressJumpSteps={{ backward: 10000, forward: 10000 }}
        customAdditionalControls={[
          <button key="speed" onClick={cycleSpeed} className="rhap_button-clear text-xs font-bold px-2 py-1 rounded hover:bg-muted transition-colors" title="Playback speed">
            {speed}x
          </button>
        ]}
        customVolumeControls={[]}
        hasDefaultKeyBindings={true}
        autoPlayAfterSrcChange={false}
        customIcons={{
          rewind: skipBackIcon,
          forward: skipForwardIcon,
        }}
      />
    </div>
  );
}

// ─── Transcript Section ───

function TranscriptSection({ conversation }: { conversation: Array<{ role: string; content: string }> }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded border hover:bg-muted transition-colors"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {open ? 'Hide Transcript' : `Show Transcript (${conversation.length} messages)`}
      </button>
      {open && (
        <div className="mt-2 max-h-72 overflow-y-auto space-y-2 rounded border bg-background p-3 text-sm">
          {conversation.map((msg, i) => (
            <div key={i} className="flex gap-2">
              <span
                className={`font-medium shrink-0 ${
                  msg.role === 'user' ? 'text-blue-600' : 'text-green-600'
                }`}
              >
                {msg.role === 'user' ? 'Caller:' : 'AI:'}
              </span>
              <span className="text-muted-foreground">{msg.content}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Expanded Row ───

function CallDetails({ callId }: { callId: string }) {
  const [detail, setDetail] = useState<CallDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCallDetail(callId).then((d) => {
      setDetail(d);
      setLoading(false);
    });
  }, [callId]);

  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading...</div>;
  }
  if (!detail) {
    return <div className="p-4 text-sm text-muted-foreground">Failed to load details</div>;
  }

  // Transcript is stored as a direct array, not { conversation: [...] }
  const conversation = Array.isArray(detail.transcript) ? detail.transcript : [];

  // Reconstruct recording URL from recording_sid if recording_url is missing
  const baseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/api$/, '');
  const recordingSource = detail.recording_url?.source
    || (detail.recording_sid ? `${baseUrl}/api/recording/proxy/${detail.recording_sid}` : null);

  const fullRecordingUrl = recordingSource || null;

  return (
    <div className="border-t bg-muted/30 px-6 py-5 space-y-5">
      {/* Summary — English */}
      {detail.summary_en && (
        <div className="rounded-lg bg-background border p-4">
          <h4 className="text-sm font-semibold mb-2">Summary</h4>
          <p className="text-sm leading-relaxed">{detail.summary_en}</p>
        </div>
      )}

      {/* Summary — Hebrew */}
      {detail.summary_he && (
        <div className="rounded-lg bg-background border p-4" dir="rtl">
          <h4 className="text-sm font-semibold mb-2 text-right">סיכום</h4>
          <p className="text-sm leading-relaxed text-right">{detail.summary_he}</p>
        </div>
      )}

      {/* Recording */}
      {fullRecordingUrl && (
        <div>
          <h4 className="text-sm font-semibold mb-2">Recording</h4>
          <RecordingPlayer recordingUrl={fullRecordingUrl} />
        </div>
      )}

      {/* Transcript */}
      {conversation.length > 0 && (
        <TranscriptSection conversation={conversation} />
      )}

      {/* Action Items */}
      {detail.action_items_structured?.length > 0 && detail.action_items_structured.some((item) => item.description_en || item.text) && (
        <div>
          <h4 className="text-sm font-semibold mb-2">Action Items</h4>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            {detail.action_items_structured.map((item, i) => {
              const text = item.description_en || item.text;
              return text ? <li key={i}>{text}</li> : null;
            })}
          </ul>
        </div>
      )}

      {/* Captured Data */}
      {detail.captured_data && Object.keys(detail.captured_data).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2">Captured Data</h4>
          <div className="text-sm text-muted-foreground space-y-1">
            {Object.entries(detail.captured_data).map(([k, v]) => (
              <div key={k}><strong>{k}:</strong> {v}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───

interface AgentOption {
  id: string;
  name: string;
}

export function CallLogPage() {
  const [calls, setCalls] = useState<Call[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [datePreset, setDatePreset] = useState('7d');
  const [filters, setFilters] = useState<CallFilters>({
    limit: 25,
    offset: 0,
    date_from: getDateFrom('7d'),
  });

  const loadCalls = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCalls(filters);
      setCalls(result.data);
      setTotal(result.total);
    } catch (e) {
      console.error('Failed to load calls:', e);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  // Load agents for filter dropdown
  useEffect(() => {
    apiRequest<{ agents: AgentOption[] }>('/studio/agents').then((res) => {
      setAgents(res.agents || []);
    }).catch(() => {});
  }, []);

  const updateFilter = (key: string, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, offset: 0 }));
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    setFilters((prev) => ({ ...prev, date_from: getDateFrom(preset), date_to: undefined, offset: 0 }));
  };

  const page = Math.floor((filters.offset || 0) / (filters.limit || 25));
  const totalPages = Math.ceil(total / (filters.limit || 25));

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Call Log</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Search phone..."
          className="w-48"
          value={filters.phone || ''}
          onChange={(e) => updateFilter('phone', e.target.value)}
        />
        <Select
          value={filters.agent_id || 'all'}
          onValueChange={(v) => updateFilter('agent_id', v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Agents" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {agents.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.direction || 'all'}
          onValueChange={(v) => updateFilter('direction', v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Direction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="outbound">Outbound</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 rounded-md border p-1">
          {[
            { value: '24h', label: '24h' },
            { value: '7d', label: '7 days' },
            { value: '30d', label: '30 days' },
            { value: 'all', label: 'All' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleDatePreset(opt.value)}
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                datePreset === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Direction</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3 font-medium">Agent</th>
              <th className="px-4 py-3 font-medium">Summary</th>
              <th className="px-4 py-3 font-medium w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : calls.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No calls found
                </td>
              </tr>
            ) : (
              calls.flatMap((call) => {
                const isExpanded = expandedIds.has(call.id);
                const summary = call.summary_en;

                const rows = [
                  <tr
                    key={call.id}
                    className="border-t cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedIds((prev) => {
                      const next = new Set(prev);
                      if (isExpanded) next.delete(call.id); else next.add(call.id);
                      return next;
                    })}
                  >
                    <td className="px-4 py-3">{formatTime(call.started_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{call.user_phone}</td>
                    <td className="px-4 py-3">
                      {call.direction === 'inbound' ? (
                        <span className="inline-flex items-center gap-1 text-blue-600">
                          <PhoneIncoming className="h-3.5 w-3.5" /> In
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-orange-600">
                          <PhoneOutgoing className="h-3.5 w-3.5" /> Out
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{formatDuration(call.duration_seconds)}</td>
                    <td className="px-4 py-3">{call.agents?.name || '—'}</td>
                    <td className="px-4 py-3 max-w-xs truncate text-muted-foreground">
                      {summary || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </td>
                  </tr>,
                ];
                if (isExpanded) {
                  rows.push(
                    <tr key={`${call.id}-detail`}>
                      <td colSpan={7}>
                        <CallDetails callId={call.id} />
                      </td>
                    </tr>
                  );
                }
                return rows;
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {total} calls total
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setFilters((p) => ({ ...p, offset: (page - 1) * (p.limit || 25) }))}
            >
              Previous
            </Button>
            <span className="flex items-center text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setFilters((p) => ({ ...p, offset: (page + 1) * (p.limit || 25) }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
